import React, { useEffect } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, FormProvider, useFormContext } from 'react-hook-form'
import InseminacaoArtificialForm from './ArtificialInseminationForm'
import { formatDateToInputValue } from 'src/utils'
import { Button, Input, SelectInput, InputCheck, SelectAnimalFetchInput, Loading, PageTitle } from 'src/components'
import { useRegisterCouple } from './hooks/useRegisterCouple'
import { VALIDATIONS } from 'src/@types/validations'
import { useStyles } from './styles'
import { CoverageProcessParams } from 'src/domain/coverage-process'
import { FormattedOption } from 'src/dto/animal.dto'
import { MontaTypes } from 'src/dto/couple-planning.dto'
import { LotAnimalsModal } from './components/LotAnimalsModal'
import { filterAnimalsByReproductionAge } from '../../utils/functions'
import ROUTES from 'src/constants/routes'
import { useNavigate } from 'react-router-dom'

export const RegisterCouple = () => {
  const methods = useForm<CoverageProcessParams>({
    resolver: yupResolver(VALIDATIONS.CoverageProcess),
    defaultValues: {
      tipoMonta: 'monta'
    }
  })

  return (
    <FormProvider {...methods}>
      <RegisterCoupleComponent />
    </FormProvider>
  )
}

const RegisterCoupleComponent: React.FC = () => {
  const {
    watch,
    setValue,
    clearErrors,
    getValues,
    reset,
    formState: { errors },
    register
  } = useFormContext<CoverageProcessParams>()

  const montaType = watch('tipoMonta')

  const {
    isLoading,
    isSaving,
    lotList,
    propertyList,
    validAnimalsFromLot,
    lotValidationError,
    showAnimalsModal,
    setShowAnimalsModal,
    onSubmit
  } = useRegisterCouple()
  const navigate = useNavigate()
  const style = useStyles()

  const clearFieldsByType = {
    monta: ['idVaca', 'idTouro', 'dataCio', 'inseminador', 'dosagem'],
    montaControlada: ['idLote', 'inseminador', 'dosagem'],
    inseminacaoArtificial: ['idLote']
  }

  useEffect(() => {
    if (!montaType) return

    const currentValues = getValues()
    const fieldsToClear = clearFieldsByType[montaType] || []

    const newValues = { ...currentValues }

    fieldsToClear.forEach((field) => {
      newValues[field] = ''
    })

    reset(newValues)
  }, [montaType, reset, getValues])

  const isArtificialMontaType = montaType === 'inseminacaoArtificial'

  return (
    <>
      <div className={style.propertiesHeader}>
        <PageTitle
          title='Processo de Cobertura'
          className={style.propertiesTitle}
          breadcrumbLink={{
            to: ROUTES.REPRODUCTION.ROOT,
            text: 'Reprodução'
          }}
        />
      </div>

      {isLoading ? (
        <div className={style.loadingContainer}>
          <Loading />
        </div>
      ) : (
        <>
          <div className={style.propertiesContainer}>
            <form onSubmit={onSubmit}>
              <fieldset className={style.formField}>
                <legend className={style.formTitle}>Cadastrar Casal</legend>
                <span className={style.infoCobertura}>Cobertura Realizada</span>
                <div className={style.containerCheckbox}>
                  <div className={style.checkControl}>
                    <InputCheck
                      id='monta'
                      value='monta'
                      label='Monta'
                      name='monta'
                      register={register}
                      error={errors?.tipoMonta?.message}
                      checked={watch('tipoMonta') === 'monta'}
                      onChange={(e) => {
                        const value = e.target.value as MontaTypes
                        setValue('tipoMonta', value)
                        clearErrors('tipoMonta')
                      }}
                    />

                    <InputCheck
                      id='monta-controladaa'
                      value='montaControlada'
                      label='Monta controlada'
                      checked={watch('tipoMonta') === 'montaControlada'}
                      name='tipoMonta_2'
                      register={register}
                      onChange={(e) => {
                        const value = e.target.value as MontaTypes
                        setValue('tipoMonta', value)
                        clearErrors('tipoMonta')
                      }}
                      className={errors?.tipoMonta?.message ? 'input-check--error' : ''}
                    />

                    <InputCheck
                      id='inseminação-artificial'
                      value='inseminacaoArtificial'
                      label='Inseminação Artificial'
                      checked={watch('tipoMonta') === 'inseminacaoArtificial'}
                      name='tipoMonta_3'
                      register={register}
                      onChange={(e) => {
                        const value = e.target.value as MontaTypes
                        setValue('tipoMonta', value)
                        clearErrors('tipoMonta')
                      }}
                      className={errors?.tipoMonta?.message ? 'input-check--error' : ''}
                    />
                  </div>

                  {validAnimalsFromLot.length > 0 && (
                    <Button
                      type='button'
                      className={style.buttonWrapper}
                      onClick={() => setShowAnimalsModal(true)}
                      outlined
                    >
                      Ver animais do lote
                    </Button>
                  )}
                </div>

                <SelectInput
                  label='Fazenda'
                  selectBy='idFazenda'
                  showBy={['descricao']}
                  options={propertyList!}
                  value={watch('idFazenda') || ''}
                  error={errors?.idFazenda?.message}
                  placeholder='Selecionar Propriedade'
                  onChange={(value: any) => {
                    setValue('idFazenda', value.idFazenda)
                    clearErrors('idFazenda')
                  }}
                />

                {isArtificialMontaType ? (
                  <InseminacaoArtificialForm lotList={lotList!} />
                ) : (
                  <>
                    {montaType === 'monta' ? (
                      <>
                        <SelectInput
                          className='halfWidth'
                          label='Lote da vaca'
                          selectBy='idLote'
                          showBy={['descricao']}
                          options={lotList!}
                          value={watch('idLote') || ''}
                          error={errors?.idLote?.message || lotValidationError || undefined}
                          placeholder='Selecionar Lote'
                          onChange={(value: any) => {
                            setValue('idLote', value.idLote)
                            clearErrors('idLote')
                          }}
                        />
                      </>
                    ) : (
                      <div className={style.containerInput}>
                        <SelectAnimalFetchInput
                          className='halfWidth'
                          label='Vaca'
                          selectBy='id'
                          showBy={['cdn', 'nome']}
                          filteredSex='Fêmea'
                          // hidden={['MT', 'VD']}
                          value={watch('idVaca') || ''}
                          filter={filterAnimalsByReproductionAge}
                          error={errors?.idVaca?.message}
                          placeholder='Selecionar vaca'
                          onChange={(item: FormattedOption) => {
                            setValue('idVaca', item.id)
                            clearErrors('idVaca')
                          }}
                        />
                      </div>
                    )}
                    <SelectAnimalFetchInput
                      className='halfWidth'
                      label='Touro'
                      selectBy='id'
                      showBy={['bgrinco', 'nome']}
                      filteredSex='Macho'
                      hidden={['MT', 'VD']}
                      value={watch('idTouro') || ''}
                      error={errors?.idTouro?.message}
                      placeholder='Selecionar touro'
                      onChange={(item: FormattedOption) => {
                        setValue('idTouro', item.id)
                        clearErrors('idTouro')
                      }}
                    />
                    {montaType === 'montaControlada' && (
                      <Input
                        type='date'
                        max='2999-12-31'
                        label='Data do cio'
                        id='data_cio'
                        error={errors?.dataCio?.message}
                        onChange={(value: any) => {
                          const date = new Date(value).toISOString()
                          setValue('dataCio', date)
                          clearErrors('dataCio')
                        }}
                        value={formatDateToInputValue(watch('dataCio') || '')}
                      />
                    )}
                    <Input
                      type='date'
                      max='2999-12-31'
                      label='Data de início'
                      id='data_inicio'
                      error={errors?.dataInicio?.message}
                      onChange={(value: any) => {
                        const date = new Date(value).toISOString()
                        setValue('dataInicio', date)
                        clearErrors('dataInicio')
                      }}
                      value={formatDateToInputValue(watch('dataInicio') || '')}
                    />

                    <Input
                      type='date'
                      max='2999-12-31'
                      label='Data Final'
                      id='data_final'
                      error={errors?.dataFim?.message}
                      onChange={(value: any) => {
                        const date = new Date(value).toISOString()
                        setValue('dataFim', date)
                        clearErrors('dataFim')
                      }}
                      value={formatDateToInputValue(watch('dataFim') || '')}
                    />
                  </>
                )}

                <div className={style.formFooter}>
                  {/* <div className={style.check}>
                    <input
                      id='register_couple'
                      type='checkbox'
                      onChange={(event: ChangeEvent<HTMLInputElement>) =>
                        setMultipleRegisterCouple(event.target.checked)
                      }
                    />
                    <label htmlFor='register_couple'>Cadastrar outra cobertura</label>
                  </div> */}

                  <div className={style.buttonContainer}>
                    <Button
                      type='button'
                      className={`${style.buttonOutlined} ${style.buttonWrapper}`}
                      onClick={() => navigate(ROUTES.REPRODUCTION.PREGNANCY_LIST)}
                      outlined
                    >
                      Cancelar
                    </Button>
                    <Button className={style.buttonWrapper} type='submit' disabled={isSaving} isLoading={isSaving}>
                      Salvar
                    </Button>
                  </div>
                </div>
              </fieldset>
            </form>
          </div>

          <LotAnimalsModal
            isOpen={showAnimalsModal}
            onClose={() => setShowAnimalsModal(false)}
            animals={validAnimalsFromLot}
          />
        </>
      )}
    </>
  )
}
